import React from "react";
import { Carousel } from "antd";
import { Card } from "react-bootstrap";
import { BsHourglassSplit, BsPinAngleFill } from "react-icons/bs";

import IndividualVideoPostTag from "./IndividualVideoPostTag";
import ImagePostTag from "./ImagePostTag";
import ProductName from "../ProductName/ProductName";
import ProductShopButton from "../ProductName/ProductShopButton";
import "./ShopCardAlignment.css";
import {
  redirectToUrl,
  openOutboundUrl,
} from "../../Utils/StayUpToDate/RenderComponent";
import IndividualYoutubePostTag from "../StayUpToDateComponent/StayUpToDateIndividuelComponent/IndividualYoutubePostTag";
import { resolveFeedCardImage } from "../../Utils/media/feedCardImage";
import {
  useTemplateMediaIndex,
  useTemplateMediaReveal,
} from "../../Screens/DashboardView/MasterTemplateEditor/useTemplateMediaReveal";
import "../../Screens/DashboardView/MasterTemplateEditor/templateMediaReveal.css";
import ProductAwareStillImage from "../ProductBank/ProductAwareStillImage";
import {
  getThumbnail,
  isLikelyVideoUrl,
  listPostMediaUrls,
} from "../../Utils/media/feedMedia";

type PostLink = {
  post_id: string;
  media_url: string;
  platform: string;
  product_name?: string;
  product_image?: string;
  thumbnail?: string;
};

const IndividualPostCarouselTag = (props: any) => {
  const {
    post,
    isLinks,
    handleRedirect,
    title,
    brand_color,
    height,
    isYoutube,
    productName,
    type,
    contentShape,
    isSoundOn,
    from,
    designTokens,
    pinnedPostIds = [],
    mediaIndex,
  } = props;

  const isPinned = pinnedPostIds?.includes(String(post?.post_id));
  const isTemplateSite = from === "website-template";
  const ctxIndex = useTemplateMediaIndex();
  const gridIndex = typeof mediaIndex === "number" ? mediaIndex : ctxIndex;

  const mediaUrls = listPostMediaUrls(post).slice(0, 10);
  const onChange = (_currentSlide: number) => {};

  const getWrapperClassName = () => {
    const baseClass = "creator-feed-image-wrapper-links";
    const classes = [baseClass];
    if (contentShape == "sq") classes.push("square-wrapper");
    else if (isYoutube) classes.push("youtube-feed-wrapper");
    return classes.join(" ");
  };

  const getImageClassName = () => {
    const baseClass = "card-image-individual";
    const classes = [baseClass];
    if (contentShape == "sq") classes.push("square-image");
    else if (isYoutube) classes.push("youtube-feed-height");
    return classes.join(" ");
  };

  // Existing redirect logic, kept as fallback
  const redirectUrl: any = redirectToUrl(post, isLinks);
  // Prefer permalink for media clicks if present
  const permalinkUrl: any = post?.permalink || redirectUrl;
  const ImagePostTagCarousel = React.memo(({ media_url, slideIndex = 0 }: any) => {
    const isFirstSlide = slideIndex === 0;
    const resolvedSrc = resolveFeedCardImage(media_url, isFirstSlide).src;
    const {
      setRef,
      activeSrc,
      onLoad,
      onError,
      wrapperClassName: revealClass,
    } = useTemplateMediaReveal({
      enabled: isTemplateSite && Boolean(media_url),
      src: resolvedSrc,
      index: typeof gridIndex === "number" ? gridIndex : slideIndex,
      priority: isFirstSlide,
    });

    if (post?.platform === "youtube" && !post?.product_image) {
      return (
        <IndividualYoutubePostTag
          link={post}
          handleRedirect={handleRedirect}
          bgColor={brand_color}
          contentShape={contentShape}
          redirectUrl={redirectUrl}
          pinnedPostIds={pinnedPostIds}
          from={from}
          designTokens={designTokens}
          showShopButton={false}
        />
      );
    }
    const mediaRedirectUrl: any = post?.permalink || redirectUrl;
    const openMedia = () => {
      if (!mediaRedirectUrl) return;
      handleRedirect(
        post?.post_id,
        isLikelyVideoUrl(post?.media_url)
          ? post?.product_image
            ? post?.product_image
            : post?.thumbnail
          : post?.product_image
          ? post?.product_image
          : post?.media_url,
        post?.platform,
        post?.product_name,
        title,
        isLinks,
        from
      );
      openOutboundUrl(mediaRedirectUrl);
    };

    const imgSrc = isTemplateSite ? activeSrc : resolvedSrc;

    return (
      <>
        {media_url === "" ? (
          <div className={getImageClassName()}>
            <BsHourglassSplit className="hourglassSplit" />
            <p>
              Work in progress: This content will be available in a few
              minutes.
            </p>
          </div>
        ) : (
          <>
            <div
              ref={isTemplateSite ? (setRef as React.Ref<HTMLDivElement>) : undefined}
              className={revealClass || undefined}
              style={{ position: "relative", cursor: "pointer" }}
              onClick={openMedia}
            >
              {isTemplateSite ? (
                imgSrc ? (
                  <Card.Img
                    variant="top"
                    src={imgSrc}
                    className={getImageClassName()}
                    loading={isFirstSlide ? "eager" : "lazy"}
                    decoding="async"
                    onLoad={onLoad}
                    onError={onError}
                    {...(isFirstSlide
                      ? ({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)
                      : {})}
                    style={{
                      marginBottom: "2%",
                      height: height,
                    }}
                  />
                ) : null
              ) : (
                <ProductAwareStillImage
                  src={resolvedSrc}
                  className={getImageClassName()}
                  alt="post"
                  post={post}
                  type={type}
                  shape={contentShape}
                  loading="lazy"
                  style={{
                    marginBottom: "0%",
                    height: height,
                  }}
                />
              )}
            </div>
          </>
        )}
      </>
    );
  });

  const RenderProductComponent = ({ post }: any) => {
    const redirectUrl: any = redirectToUrl(post, isLinks);

    const trackShopClick = () => {
      // Shop CTA must always track as shop — never inherit carousel isLinks=false.
      handleRedirect(
        post?.post_id,
        isLikelyVideoUrl(post?.media_url)
          ? post?.product_image
            ? post?.product_image
            : post?.thumbnail
          : post?.product_image
          ? post?.product_image
          : post?.media_url,
        post?.platform,
        post?.product_name,
        title,
        true,
        from
      );
    };

    return (
      <>
        <ProductName type={type} post={post} designTokens={designTokens} />
        <span className="carousel-shop-btn-wrap">
          <ProductShopButton
            isLinks={isLinks}
            isIndividual={false}
            type={type}
            post={post}
            isCarousel={true}
            onBeforeNavigate={trackShopClick}
            designTokens={designTokens}
          />
        </span>
      </>
    );
  };

  const wrapperClass = `${getWrapperClassName()} design-token-dots image-post-carousel`;

  return (
    <span>
      <div className={wrapperClass} style={{ position: "relative" }}>
        {isPinned && (
          <div className="pinned-badge" aria-label="Pinned post" style={{right:"8px"}}>
            <BsPinAngleFill />
          </div>
        )}

        {mediaUrls.some((media_url: string) => isLikelyVideoUrl(media_url)) ? (
          <>
            <Carousel
              afterChange={onChange}
              dotPosition="bottom"
              infinite={mediaUrls.length > 1}
              speed={isTemplateSite ? 0 : undefined}
              waitForAnimate={false}
              draggable={mediaUrls.length > 1}
              style={isTemplateSite ? { marginBottom: 0 } : { marginBottom: "3px" }}
            >
              {mediaUrls.map((media_url: string, index: number) => (
                <div key={index}>
                  {isLikelyVideoUrl(media_url) ? (
                    <IndividualVideoPostTag
                      media_url={media_url}
                      productName={productName}
                      post={post}
                      mediaIndex={index}
                      thumbnail={getThumbnail(post, index)}
                      handleRedirect={handleRedirect}
                      title={title}
                      isSoundOn={isSoundOn}
                      type={type}
                      contentShape={contentShape}
                      isCarousel={true}
                      isLinks={isLinks}
                      brand_color={brand_color}
                      from={from}
                      isYoutube={
                        Boolean(isYoutube) ||
                        String(post?.platform || "").toLowerCase() ===
                          "youtube"
                      }
                      pinnedPostIds={pinnedPostIds}
                      designTokens={designTokens}
                    />
                  ) : post.platform === "youtube" && !post.product_image ? (
                    <IndividualYoutubePostTag
                      link={post}
                      handleRedirect={(link: PostLink) =>
                        handleRedirect(
                          link.post_id,
                          isLikelyVideoUrl(link.media_url)
                            ? link.product_image || link.thumbnail
                            : link.product_image || link.media_url,
                          link.platform,
                          link.product_name,
                          title,
                          isLinks,
                          from
                        )
                      }
                      bgColor={brand_color || "#000"}
                      contentShape={contentShape}
                      redirectUrl={redirectUrl}
                      source="individual-carousel"
                      from={from}
                      pinnedPostIds={pinnedPostIds}
                      designTokens={designTokens}
                      showShopButton={false}
                    />
                  ) : (
                    <ImagePostTag
                      data={post}
                      title={title}
                      media_url={media_url}
                      isVideo={true}
                      handleRedirect={handleRedirect}
                      productName={productName}
                      isCarousel={true}
                      contentShape={contentShape}
                      from={from}
                      designTokens={designTokens}
                      pinnedPostIds={pinnedPostIds}
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  )}
                </div>
              ))}
            </Carousel>

            <RenderProductComponent post={post} />
          </>
        ) : (
          <>
            <Carousel
              afterChange={onChange}
              dotPosition="bottom"
              infinite={mediaUrls.length > 1}
              speed={isTemplateSite ? 0 : undefined}
              waitForAnimate={false}
              draggable={mediaUrls.length > 1}
              style={isTemplateSite ? { marginBottom: 0 } : { marginBottom: "3px" }}
            >
              {mediaUrls.map((media_url: any, index: any) => (
                <div key={index}>
                  {post.platform === "youtube" && !post.product_image ? (
                    <IndividualYoutubePostTag
                      link={post}
                      handleRedirect={(link: PostLink) =>
                        handleRedirect(
                          link.post_id,
                          isLikelyVideoUrl(link.media_url)
                            ? link.product_image || link.thumbnail
                            : link.product_image || link.media_url,
                          link.platform,
                          link.product_name,
                          title,
                          isLinks,
                          from
                        )
                      }
                      bgColor={brand_color || "#000"}
                      contentShape={contentShape}
                      redirectUrl={redirectUrl}
                      source="individual-carousel"
                      from={from}
                      pinnedPostIds={pinnedPostIds}
                      designTokens={designTokens}
                      showShopButton={false}
                    />
                  ) : (
                    <ImagePostTagCarousel media_url={media_url} slideIndex={index} designTokens={designTokens} />
                  )}
                </div>
              ))}
            </Carousel>

            <RenderProductComponent post={post} />
          </>
        )}
      </div>
    </span>
  );
};

export default IndividualPostCarouselTag;

